// src/components/frontpage/columns/EventsColumn.jsx
// Event cards for the MultiViewGrid front page column / mobile Events tab (locality context).
// Uses real Supabase data (articles + event_details) via the `data` prop.
//
// FUTURE/PAST (Events tab): this column owns the roster split. It receives the FULL list of the
// locality's events (the parent no longer pre-filters to the feed window) and splits them with
// hasEventEnded — Future (default) = not ended, soonest first; Past = ended, most recent first.
// display_from is ignored here (it is a feed-scheduling control, not a roster filter).
//
// Dense "noticeboard" layout: thumbnail + title + time·venue, grouped by day with slim separators.

import React, { useMemo, useState } from 'react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAuth } from '../../../contexts/AuthContext';
import ModeColumn from './ModeColumn';
import MobileActionLine from '../../core/HeadersAndFooters/MobileActionLine';
import { parseEventDate, hasEventEnded } from '../../../utils/eventDateTime';
import { useTagHouseFallback } from '../../../hooks/useTagHouseFallback';
import { useExpandInPlace } from '../../../hooks/useExpandInPlace';
import { useScrollIntoViewOnOpen } from '../../../hooks/useScrollIntoViewOnOpen';
import EventsMiniCard from '../../events/EventsMiniCard';
import MoreButton from './MoreButton';

// Inline fallback for events without images
const FALLBACK_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='40' viewBox='0 0 48 40' preserveAspectRatio='xMidYMid slice' fill='%23e5e7eb'%3E%3Crect width='48' height='40'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='12'%3E%F0%9F%93%85%3C/text%3E%3C/svg%3E";

// paging (Roadmap #104, addendum 5): { busy, future: { hasMore, onLoadMore, loading }, past: {…} } or
// null. Load More asks for the next 50 by rank of the window on view; the list stays in date order,
// so new events can appear between ones already shown.
const EventsColumn = ({ data = [], onClick, hideColumnHeader = false, visitBoundaryId = null, paging = null }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('frontpage');
  const { requireAuth } = useAuth();
  // R4 house fallback (image-layer build) — null until fetched, so the inline
  // SVG below keeps rendering as the interim default, no blank-to-image flash.
  const houseFallback = useTagHouseFallback();

  // Future (default) | Past
  const [view, setView] = useState('future');

  // MiniCard open/close identity (one-open-per-column, per the tier ruling) + scroll-into-view
  // on open. Both are the shared, reusable pieces named in the build brief — not reinvented
  // here (docs/briefs/2026-08-15-events-minicard-build-1.md).
  // Named toggleCard, not toggle — `toggle` below (line ~125) is already the Future|Past pill JSX.
  const { openId, isOpen, toggle: toggleCard } = useExpandInPlace();
  const { setRef } = useScrollIntoViewOnOpen(openId);


  // Format the start time for the card line: "19:00". The day is in the date separator above.
  const formatEventTime = (dateStr) => {
    const date = parseEventDate(dateStr);
    if (!date) return '';
    return date.toLocaleTimeString(formattingLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Format a date separator header: "Wednesday, 24 June" (omit year unless different)
  const formatDateSeparator = (dateStr) => {
    if (!dateStr) return '';
    const date = parseEventDate(dateStr);
    if (!date) return '';
    const now = new Date();
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    if (date.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    return date.toLocaleDateString(formattingLocale, options);
  };

  // Get a date-only key (YYYY-MM-DD) in local timezone for grouping
  const getDateKey = (dateStr) => {
    if (!dateStr) return 'none';
    const d = parseEventDate(dateStr);
    if (!d) return 'none';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Null-safe start-date comparators (events without a start sink to the bottom)
  const byStartAsc = (a, b) => {
    const da = parseEventDate(a.start_date);
    const db = parseEventDate(b.start_date);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da - db;
  };

  // Split the full roster into Ongoing / Upcoming / Past (display_from ignored — this is the
  // roster, not the feed). Ended is the one boundary test — hasEventEnded, unchanged — so an
  // event lands in exactly one bucket: ended -> Past; not ended and already started -> Ongoing;
  // not ended and not yet started -> Upcoming. Ongoing surfaces under "Happening now" in the
  // Future view (below) and never appears in Past, satisfying the exclusive-placement ruling.
  const { ongoing, upcoming, past } = useMemo(() => {
    const ong = [];
    const upc = [];
    const pst = [];
    const now = new Date();
    for (const event of data) {
      if (hasEventEnded(event, now)) {
        pst.push(event);
      } else {
        const started = event.start_date && parseEventDate(event.start_date) <= now;
        (started ? ong : upc).push(event);
      }
    }
    ong.sort(byStartAsc);
    upc.sort(byStartAsc);                 // soonest first
    pst.sort((a, b) => -byStartAsc(a, b)); // most recent first
    return { ongoing: ong, upcoming: upc, past: pst };
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group for display: Past groups by calendar day as before. Future pins a "Happening now"
  // group (ongoing events, at/above Today) ahead of the day-grouped upcoming events — ongoing
  // events display under that grouping instead of under their (already-passed) start-date
  // header, per Michael's ruling.
  const groupedEvents = useMemo(() => {
    const groupByDay = (list) => {
      const groups = [];
      let currentKey = null;
      for (const event of list) {
        const key = getDateKey(event.start_date);
        if (key !== currentKey) {
          currentKey = key;
          groups.push({ dateKey: key, label: formatDateSeparator(event.start_date), events: [] });
        }
        groups[groups.length - 1].events.push(event);
      }
      return groups;
    };

    if (view === 'past') return groupByDay(past);

    const groups = [];
    if (ongoing.length > 0) {
      // "Happening now" carries today's date after the label (Michael, 16 Aug, mobile-tab-
      // subheaders brief), same format as the sibling day headers — reuses formatDateSeparator
      // itself (parseEventDate already accepts a raw Date instance), no second formatter.
      groups.push({
        dateKey: 'happening-now',
        label: `${t('events:happening_now')} — ${formatDateSeparator(new Date())}`,
        events: ongoing
      });
    }
    return groups.concat(groupByDay(upcoming));
  }, [view, ongoing, upcoming, past, formattingLocale, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNewEvent = () => {
    requireAuth('gate_body_add_event', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'new-event', mode: 'add' }
      }));
    });
  };

  // Event creation is not available while visiting (Change 3, addendum-1 §A/§C) — hidden, both
  // in the mobile sub-header and the desktop column header (used by both LocalityFrontPage and
  // MobileFrontPage's Events tab — this component has no group-scoping, so it is Locality-only).
  const isVisiting = !!visitBoundaryId;

  // Compact two-segment Future|Past pill — themed by context accent (mauve group / blue locality).
  // Rendered via ModeColumn's headerRight slot when the header is shown (desktop). On mobile,
  // hideColumnHeader suppresses headerRight entirely (ModeColumn.jsx), so the same pill is also
  // rendered again, centred, in this column's own pinned mobile sub-header — per the mobile-tab-
  // subheaders brief (docs/briefs/2026-08-16-mobile-tab-subheaders.md), which supersedes addendum
  // 1's "first row of the scrolling list body" placement (that scrolled away with content; the
  // sub-header is pinned, matching the tab bar it now sits directly under).
  const toggle = (
    <div style={{
      display: 'inline-flex', borderRadius: '999px', overflow: 'hidden',
      border: `1px solid ${theme.primary}`, flexShrink: 0
    }}>
      {[['future', t('events:toggle_future')], ['past', t('events:toggle_past')]].map(([key, label]) => {
        const active = view === key;
        return (
          <button
            key={key}
            onClick={() => setView(key)}
            style={{
              border: 'none', cursor: 'pointer', padding: '3px 12px',
              fontFamily: 'OpenSauceSans, sans-serif', fontSize: '12px', fontWeight: 700,
              background: active ? theme.primary : 'transparent',
              color: active ? '#fff' : theme.primary
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  // Pinned mobile sub-header — create (left), toggle (centred), search (right). Retires the old
  // shared HeaderButtonsFeed Row 3 for this tab; each panel now owns its own sub-header (brief
  // §Scope). Rendered as a sibling above ModeColumn, not inside its scrolling body, so it stays
  // pinned under the tab bar rather than scrolling away with the list.
  const mobileSubHeader = (
    <MobileActionLine
      contextColor={theme.primary}
      createLabel={t('mobile_create_event')}
      onCreate={isVisiting ? undefined : handleNewEvent}
      center={toggle}
      topBorder={false}
      sideBorders={false}
      searchPlaceholder={t('ui:search_articles')}
      onSearch={(term) => window.dispatchEvent(new CustomEvent('ournet:search-articles', { detail: { searchTerm: term } }))}
    />
  );

  // Compact row: thumbnail + title + time·venue, no borders (sits in a 2-per-line grid cell).
  // MicroCard <-> MiniCard is a REPLACE, not a supplement (docs/briefs/2026-08-15-
  // events-minicard-replace-and-border.md) — open renders the MiniCard in place of the row, not
  // alongside it, so the event appears once. The wrapping div carries the scroll-into-view ref;
  // since it wraps whichever single element (row or MiniCard) is currently rendered, no change
  // to useScrollIntoViewOnOpen itself is needed for the swap.
  const eventRow = (event) => {
    const open = isOpen(event.id);
    return (
      <div key={event.id} ref={setRef(event.id)}>
        {open ? (
          <EventsMiniCard
            event={event}
            onViewDetails={() => onClick?.(event)}
            onCollapse={() => toggleCard(event.id)}
          />
        ) : (
          <div
            onClick={() => toggleCard(event.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '9px',
              padding: '7px', minWidth: 0,
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; }}
          >
            <img
              src={event.cover_image_url || houseFallback || FALLBACK_IMAGE}
              alt=""
              loading="lazy"
              // §5a — never crop the illustration's own border; contain + this
              // card's background (white, per its container `background` above) as
              // the letterbox fill.
              style={{ width: '48px', height: '40px', objectFit: 'contain', backgroundColor: 'white', flexShrink: 0, borderRadius: '6px' }}
              onError={(e) => { e.target.onerror = null; e.target.src = houseFallback || FALLBACK_IMAGE; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'OpenSauceSans, sans-serif', fontWeight: '600', fontSize: '13px',
                color: '#1f2937', lineHeight: '1.25',
                overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
              }}>
                {event.title}
              </div>
              <div style={{
                fontFamily: 'OpenSauceSans, sans-serif', fontSize: '11px', color: '#6b7280',
                marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
              }}>
                {formatEventTime(event.start_date)}
                {event.venue_name ? ` · ${event.venue_name}` : ''}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {hideColumnHeader && mobileSubHeader}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ModeColumn
          title={t('column_events')}
          themeColor={theme.primary}
          hideColumnHeader={hideColumnHeader}
          onAdd={isVisiting ? undefined : handleNewEvent}
          addLabel={t('new_event')}
          isEmpty={data.length === 0}
          emptyLabel={t('empty_events')}
          headerRight={toggle}
          footer={paging?.[view] ? (
            <MoreButton
              hasMore={paging[view].hasMore}
              onClick={paging[view].onLoadMore}
              label={paging[view].loading ? t('common:loading') : t('common:load_more')}
              color={theme.primary}
              iconName="ChevronDown"
              disabled={Boolean(paging.busy)}
            />
          ) : undefined}
        >
          {groupedEvents.length === 0 ? (
            <div style={{
              fontFamily: 'OpenSauceSans, sans-serif', fontSize: '12px', color: '#9ca3af',
              textAlign: 'center', padding: '20px 8px'
            }}>
              {t('empty_events')}
            </div>
          ) : (
            groupedEvents.map(grp => (
              <div key={grp.dateKey} style={{ marginBottom: '10px' }}>
                {grp.label && (
                  <div style={{
                    fontFamily: 'OpenSauceSans, sans-serif', fontWeight: '700', fontSize: '13px',
                    color: theme.primary, background: 'white', border: `1px solid ${theme.primary}`,
                    textTransform: 'uppercase', letterSpacing: '0.3px', padding: '4px 8px',
                    borderRadius: '6px', margin: '0 0 6px', lineHeight: '1.3'
                  }}>
                    {grp.label}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {grp.events.map(eventRow)}
                </div>
              </div>
            ))
          )}
        </ModeColumn>
      </div>
    </div>
  );
};

export default EventsColumn;
